import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["modal", "searchInput", "results", "selectedContainer", "template"];

  connect() {
    this.selectedIds = new Set();
    // Populate selectedIds from existing hidden fields
    this.element.querySelectorAll("input[name*='[child_product_id]']").forEach((input) => {
      if (input.value) {
        this.selectedIds.add(parseInt(input.value));
      }
    });
  }

  open(event) {
    event.preventDefault();
    this.modalTarget.classList.remove("hidden");
    // Fetch initial results
    this.loadResults();
  }

  close() {
    this.modalTarget.classList.add("hidden");
  }

  // Debounced search
  search() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.loadResults();
    }, 300);
  }

  loadResults(event) {
    // Use page from Stimulus action params (pagination), default to 1
    const page = event?.params?.page || 1;
    const query = this.searchInputTarget.value;
    const url = new URL(this.element.dataset.productPickerSearchUrl, window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("page", page);
    // Send current product ID to exclude self from results
    if (this.element.dataset.productPickerCurrentProductId) {
      url.searchParams.set("id", this.element.dataset.productPickerCurrentProductId);
    }
    // Send currently selected IDs so server can mark them as "Added"
    url.searchParams.set("selected_ids", [...this.selectedIds].join(","));

    fetch(url, {
      headers: { "Accept": "text/vnd.turbo-stream.html" },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
      });
  }

  addProduct(event) {
    const button = event.currentTarget;
    const productId = parseInt(button.dataset.productId);
    const productTitle = button.dataset.productTitle;

    if (this.selectedIds.has(productId)) return;

    this.selectedIds.add(productId);

    // Clone template and insert into the form
    const template = this.templateTarget.content.cloneNode(true);
    const row = template.querySelector(".nested-form-row");

    // Replace NEW_RECORD with unique index
    const newIndex = new Date().getTime();
    row.innerHTML = row.innerHTML.replace(/NEW_RECORD/g, newIndex);

    // Set the child_product_id hidden field value
    const hiddenInput = row.querySelector("input[name*='[child_product_id]']");
    if (hiddenInput) {
      hiddenInput.value = productId;
    }

    this.selectedContainerTarget.appendChild(row);

    // Populate the row: id, slug, title, link
    const idEl = row.querySelector(".selected-product-id");
    const slugEl = row.querySelector(".selected-product-slug");
    const titleEl = row.querySelector(".selected-product-title");
    const linkEl = row.querySelector(".selected-product-link");
    const linkTextEl = row.querySelector(".selected-product-link-text");

    if (idEl) idEl.textContent = `#${productId}`;
    if (slugEl) slugEl.textContent = `/${button.dataset.productSlug}`;
    if (titleEl) titleEl.textContent = productTitle;
    if (linkEl && button.dataset.productSlug && button.dataset.productUrlTitle) {
      const fullUrl = `${window.location.origin}/tech/${button.dataset.productSlug}-${button.dataset.productUrlTitle}`;
      linkEl.href = fullUrl;
      if (linkTextEl) linkTextEl.textContent = fullUrl;
      linkEl.classList.remove("hidden");
    }

    // Toggle card state in modal: hide "Add" button, show "Added ✓ / Remove"
    const card = button.closest(".border-slate-200");
    if (card) {
      const addBtn = card.querySelector(".add-product-btn");
      const addedState = card.querySelector(".added-product-state");
      if (addBtn) addBtn.classList.add("hidden");
      if (addedState) {
        addedState.classList.remove("hidden");
        addedState.classList.add("flex");
      }
    }
  }

  removeFromModal(event) {
    const button = event.currentTarget;
    const productId = parseInt(button.dataset.productId);

    // Remove the form row
    const rows = this.selectedContainerTarget.querySelectorAll(".nested-form-row");
    rows.forEach((row) => {
      const input = row.querySelector("input[name*='[child_product_id]']");
      if (input && parseInt(input.value) === productId) {
        row.remove();
      }
    });

    // Remove from selectedIds
    this.selectedIds.delete(productId);

    // Toggle card state back: show "Add" button, hide "Added ✓ / Remove"
    const card = button.closest(".border-slate-200");
    if (card) {
      const addBtn = card.querySelector(".add-product-btn");
      const addedState = card.querySelector(".added-product-state");
      if (addBtn) addBtn.classList.remove("hidden");
      if (addedState) {
        addedState.classList.remove("flex");
        addedState.classList.add("hidden");
      }
    }
  }

  remove(event) {
    event.preventDefault();
    const row = event.currentTarget.closest(".nested-form-row");
    const destroyInput = row.querySelector("input[name*='[_destroy]']");

    if (destroyInput) {
      // Existing record: mark for destruction
      destroyInput.value = "1";
      row.classList.add("hidden");
    } else {
      // New record: remove from DOM
      row.remove();
    }

    // Remove from selectedIds
    const productIdInput = row.querySelector("input[name*='[child_product_id]']");
    if (productIdInput && productIdInput.value) {
      this.selectedIds.delete(parseInt(productIdInput.value));
    }

    // Toggle card state back in modal if it's open
    const productId = productIdInput ? parseInt(productIdInput.value) : null;
    if (productId) {
      this.element.querySelectorAll(".add-product-btn").forEach((btn) => {
        if (parseInt(btn.dataset.productId) === productId) {
          btn.classList.remove("hidden");
          const addedState = btn.nextElementSibling;
          if (addedState) {
            addedState.classList.remove("flex");
            addedState.classList.add("hidden");
          }
        }
      });
    }
  }

  // Close modal on backdrop click
  clickOutside(event) {
    if (event.target === this.modalTarget) {
      this.close();
    }
  }
}